import { showToast, Stack } from "@certn/cascadia";
import { PrimaryButton, Select, SelectItem } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { MonitoringEnrollmentDemoResponse } from "../types";
import {
  useEnrollmentDemoResponse,
  useSaveEnrollmentDemoResponse,
} from "../useMonitoringApi";

const editorStyle = css({
  minWidth: "min(100%, 16rem)",
  maxWidth: "32rem",
});

const controlsRowStyle = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--space-3)",
  alignItems: "flex-end",
});

const selectFieldStyle = css({
  flex: "1 1 10rem",
  minWidth: "8rem",
});

interface Props {
  enrollmentId: string;
}

function buildDraftClaims(
  specs: NonNullable<
    MonitoringEnrollmentDemoResponse["demo_response_claim_specs"]
  >,
  currentClaims: Record<string, string>
): Record<string, string> {
  const draft: Record<string, string> = {};
  for (const spec of specs) {
    const key = String(spec.key);
    draft[key] = currentClaims[key] ?? "";
  }
  return draft;
}

export function MonitoringDemoResponseEditor({ enrollmentId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, error, mutate } =
    useEnrollmentDemoResponse(enrollmentId);
  const saveDemoResponse = useSaveEnrollmentDemoResponse();
  const [draftClaims, setDraftClaims] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const specs = (data?.demo_response_claim_specs ?? []) as Array<{
    key: string;
    label: string;
    options: { value: string; label: string }[];
  }>;
  const currentClaims = (data?.demo_response_claims ?? {}) as Record<
    string,
    string
  >;

  useEffect(() => {
    if (!data) {
      return;
    }
    setDraftClaims(
      buildDraftClaims(
        data.demo_response_claim_specs as Array<{
          key: string;
          label: string;
          options: { value: string; label: string }[];
        }>,
        data.demo_response_claims as Record<string, string>
      )
    );
  }, [data]);

  const hasChanges = specs.some((spec) => {
    const claimKey = spec.key;
    return (draftClaims[claimKey] ?? "") !== (currentClaims[claimKey] ?? "");
  });

  async function handleSave() {
    setIsSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(draftClaims).filter(([, value]) => value)
      );
      await saveDemoResponse(enrollmentId, payload);
    } catch {
      showToast(t("Unable to save next demo response"), { type: "error" });
      return;
    } finally {
      setIsSaving(false);
    }

    try {
      await mutate();
    } catch {
      showToast(t("Next demo response saved, but refresh failed"), {
        type: "info",
      });
    }
    showToast(t("Next demo response saved"), { type: "success" });
  }

  if (error) {
    return null;
  }

  if (isLoading || specs.length === 0) {
    return null;
  }

  return (
    <Stack
      gap="space-3"
      className={editorStyle}
      testID="monitoring-demo-response-editor"
    >
      <div className={controlsRowStyle}>
        {specs.map((spec) => {
          const claimKey = spec.key;
          const claimLabel = spec.label;
          const options = spec.options ?? [];

          return (
            <div key={claimKey} className={selectFieldStyle}>
              <Select
                label={claimLabel}
                selectedKey={draftClaims[claimKey] || null}
                onSelectionChange={(key) =>
                  setDraftClaims((current) => ({
                    ...current,
                    [claimKey]: key ? String(key) : "",
                  }))
                }
                testID={`monitoring-demo-response-select-${claimKey}`}
              >
                {options.map((option) => (
                  <SelectItem key={option.value} id={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          );
        })}
        <PrimaryButton
          onPress={handleSave}
          isDisabled={!hasChanges || isSaving}
          testID="monitoring-demo-response-save-button"
        >
          {t("Save")}
        </PrimaryButton>
      </div>
    </Stack>
  );
}
